import { Injectable, inject, signal, WritableSignal } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { catchError, map, of, timer, switchMap, Subscription } from 'rxjs';
import { AuthService } from './api/auth.service';

@Injectable({
  providedIn: 'root'
})
export class MaintenanceService {
  /**
   * Signal indicating if the application is currently in maintenance mode.
   * True: Backend is unreachable.
   * False: Normal operation.
   */
  public readonly maintenanceMode: WritableSignal<boolean> = signal(false);

  /**
   * Signal indicating if the backend has recovered and we are about to reload.
   * Used to show the success animation.
   */
  public readonly recovered: WritableSignal<boolean> = signal(false);

  /**
   * Signal showing how many consecutive health checks have passed (0–REQUIRED_CONSECUTIVE_SUCCESSES).
   * Displayed in the maintenance screen so users see recovery progress.
   */
  public readonly recoveryProgress: WritableSignal<number> = signal(0);

  private readonly POLL_INTERVAL_MS = 10000; // Check every 10 seconds
  private readonly HEALTH_ENDPOINT = `${environment.apiUrl}`; // Public health check
  private readonly REQUIRED_CONSECUTIVE_SUCCESSES = 3;
  private readonly REQUIRED_FAILURES_TO_ACTIVATE = 2;
  private readonly FAILURE_WINDOW_MS = 30_000; // 30 seconds

  private readonly httpClient = new HttpClient(inject(HttpBackend));
  private readonly authService = inject(AuthService);
  private pollingSubscription: Subscription | null = null;
  private isChecking = false;
  private consecutiveSuccessCount = 0;
  private recentFailureTimestamps: number[] = [];

  /**
   * Records a backend failure. Activates maintenance mode only after
   * REQUIRED_FAILURES_TO_ACTIVATE failures within FAILURE_WINDOW_MS.
   * This prevents a single transient error from locking the user into the break screen.
   */
  recordFailure(): void {
    const now = Date.now();

    // Prune timestamps outside the window
    this.recentFailureTimestamps = this.recentFailureTimestamps
      .filter(ts => now - ts < this.FAILURE_WINDOW_MS);

    this.recentFailureTimestamps.push(now);

    if (this.recentFailureTimestamps.length >= this.REQUIRED_FAILURES_TO_ACTIVATE) {
      this.enable();
    }
  }

  /**
   * Triggers an immediate health check (user-initiated "try again").
   * Applies the same success/failure logic as the polling loop.
   */
  checkNow(): void {
    if (!this.maintenanceMode() || this.recovered() || this.isChecking) {
      return;
    }
    this.checkHealth().subscribe(isHealthy => this.handleHealthResult(isHealthy));
  }

  /**
   * Activates maintenance mode and starts polling for recovery.
   */
  private enable(): void {
    if (this.maintenanceMode()) {
      return;
    }

    this.maintenanceMode.set(true);
    this.startRecoveryPolling();
  }

  /**
   * Disable maintenance mode and stop polling.
   */
  private disable(): void {
    if (!this.maintenanceMode()) {
      return;
    }

    console.log('[Maintenance] Backend recovered. Resuming normal operation.');
    this.maintenanceMode.set(false);
    this.recovered.set(false);
    this.recoveryProgress.set(0);
    this.consecutiveSuccessCount = 0;
    this.recentFailureTimestamps = [];
    this.stopRecoveryPolling();
  }

  /**
   * Start polling the backend to see when it comes back online.
   * Requires REQUIRED_CONSECUTIVE_SUCCESSES consecutive healthy checks before recovery.
   */
  private startRecoveryPolling(): void {
    if (this.pollingSubscription) {
      return;
    }

    this.consecutiveSuccessCount = 0;
    this.recoveryProgress.set(0);

    this.pollingSubscription = timer(2000, this.POLL_INTERVAL_MS).pipe(
      switchMap(() => this.checkHealth())
    ).subscribe((isHealthy) => this.handleHealthResult(isHealthy));
  }

  /**
   * Applies the shared success/failure logic for a health check result.
   */
  private handleHealthResult(isHealthy: boolean): void {
    if (this.recovered()) {
      return;
    }

    if (isHealthy) {
      this.consecutiveSuccessCount++;
      this.recoveryProgress.set(this.consecutiveSuccessCount);
      console.log(
        `[Maintenance] Health check passed (${this.consecutiveSuccessCount}/${this.REQUIRED_CONSECUTIVE_SUCCESSES})`
      );

      if (this.consecutiveSuccessCount >= this.REQUIRED_CONSECUTIVE_SUCCESSES) {
        // Backend is stable — show success state, then reload
        this.recovered.set(true);

        setTimeout(() => {
          this.disable();
          window.location.reload();
        }, 2000);
      }
    } else {
      // Reset counter on any failure
      this.consecutiveSuccessCount = 0;
      this.recoveryProgress.set(0);
    }
  }

  private stopRecoveryPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  /**
   * Performs a lightweight health check.
   * Uses a two-tier approach:
   *   - Not logged in: GET /api (public endpoint)
   *   - Logged in: GET /api/member (authenticated, tests full pipeline)
   * Returns Observable<boolean>: true if healthy, false if still down.
   */
  private checkHealth() {
    if (this.isChecking) return of(false);
    this.isChecking = true;

    const isLoggedIn = this.authService.isAuthenticated();
    const endpoint = isLoggedIn
      ? `${this.HEALTH_ENDPOINT}/member`
      : this.HEALTH_ENDPOINT;

    return this.httpClient.get(endpoint, { responseType: 'text' }).pipe(
      map(() => true),
      catchError(error => {
        // 401/403/404 → server IS up, just answering with an error
        // 0/500/502/503/504 → server is still down
        if ([0, 500, 502, 503, 504].includes(error.status)) {
          return of(false);
        }
        return of(true);
      }),
      map(res => {
        this.isChecking = false;
        return res;
      })
    );
  }
}
